import React from "react";
import DialogContent from "@mui/material/DialogContent";
import styled from "styled-components";
import { fonts } from "./mixins.js";
import { Clear } from "../icons/Clear";
import { ResponsiveModal, modalTitleId } from "./ResponsiveModal";

interface CenteredOverlayProps {
  open: boolean;
  handleClose: () => void;
  id: string;
  titleText: string;
  icon?: React.ReactNode;
  size?: "sm" | "md" | "lg";
  children: React.ReactNode;
}

const CloseRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const CloseButton = styled.span`
  cursor: pointer;
  line-height: 0;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`;

const Title = styled.h2`
  ${fonts.title()}
  margin: 0;

  line-height: normal;
  padding: 2px;
`;

const DrawerPuller = styled.div`
  width: 48px;
  height: 4px;
  background: #9897a9;
  border-radius: 3px;
  margin: 0 auto 24px;
`;

const StyledDialogContent = styled(DialogContent)`
  text-align: center;
  padding: 0 !important;

  p {
    line-height: normal;
  }
`;

const DrawerChildren = styled.div`
  text-align: center;

  p {
    margin: 0;
    padding-bottom: 32px;
  }
`;

const DrawerContent = styled.div`
  background: #fff;
  padding: 16px;

  p {
    line-height: normal;
  }
`;

// "Alert" modal: close icon on its own row, then an optional icon and a
// centered title above centered content. For short info/confirmation messages.
export function CenteredOverlay({
  open,
  handleClose,
  id,
  titleText,
  icon,
  size = "lg",
  children,
}: CenteredOverlayProps) {
  const dialogWidth = size === "sm" ? 452 : size === "md" ? 900 : 1212;

  const titleSection = (
    <TitleSection>
      {icon}
      <Title id={modalTitleId(id)}>{titleText}</Title>
    </TitleSection>
  );

  const drawerContent = (
    <DrawerContent>
      <DrawerPuller />
      {titleSection}
      <DrawerChildren>{children}</DrawerChildren>
    </DrawerContent>
  );

  const dialogContent = (
    <>
      <CloseRow>
        <CloseButton id="close-btn" onClick={handleClose}>
          <Clear />
        </CloseButton>
      </CloseRow>
      {titleSection}
      <StyledDialogContent>{children}</StyledDialogContent>
    </>
  );

  return (
    <ResponsiveModal
      open={open}
      onClose={handleClose}
      id={id}
      width={dialogWidth}
      radius={16}
      dialogPaperSx={{ padding: "24px" }}
      drawerContent={drawerContent}
      dialogContent={dialogContent}
    />
  );
}
